// Per-map configuration loaded from a config.json blob (S3) and consumed by
// MapLegacy to drive layer color schemes, hatching, opacity, legend aliases, and
// which tract layers the project shows. When no config is present (or it fails to
// load), MapLegacy falls back to the hardcoded Default*MapColors arrays.

import { CONTRACT_LAYER_TYPES, contractLayerTitle, LEASEHOLD_LAYER_TITLE } from './ContractLayers';

export interface ITractLayerConfig {
    alias?: string;
    predefinedColors?: { [value: string]: number[] };
    // false hides the layer on this project's map; absent or true shows it. Set from the GIS
    // portal's Map Layers checkboxes.
    enabled?: boolean;
}

export interface IMapConfig {
    tractLayersConfig?: {
        MinCat?: ITractLayerConfig;
        TypeCheck?: ITractLayerConfig;
        AreaName?: ITractLayerConfig;
        Lessee?: ITractLayerConfig;
        // A contract type's own layer, keyed by its prefix in the tracts file (Brine, Solar,
        // RightOfWay, ...). Optional: without one the layer takes Lessee's owner colors.
        [contractLayerPrefix: string]: ITractLayerConfig | undefined;
    };
    assignableColorList?: number[][];
    TractOutlineColor?: number[];
    BaseHatchingWidth?: number;
    tractOpacity?: number;
}

export type LayerKey = 'MinCat' | 'TypeCheck' | 'Lessee';

// Fixed mapping from config layer key to the LandHawk-normalized GeoJSON
// property the renderer uses. AreaName is intentionally excluded (no current layer).
export const LAYER_KEY_TO_FIELD: Record<LayerKey, string> = {
    MinCat: 'Contact_Category',
    TypeCheck: 'Title_Status',
    Lessee: 'WI_Owner',
};

// Every tract layer the map can draw, keyed the way tractLayersConfig is, in the order the GIS
// portal lists them, each under the name its legend shows. A contract layer (keyed by its prefix)
// still only appears on a project whose tracts file carries that contract type.
export const TRACT_LAYERS: Array<{ key: string; label: string }> = [
    { key: 'MinCat', label: 'Contact Category' },
    { key: 'TypeCheck', label: 'Title Status' },
    { key: 'Lessee', label: LEASEHOLD_LAYER_TITLE },
    ...CONTRACT_LAYER_TYPES.map((type) => ({ key: type.prefix, label: contractLayerTitle(type) })),
];

const MAP_LAYER_ID_TO_KEY: Record<string, string> = {
    'tracts-contact-category': 'MinCat',
    'tracts-title-status': 'TypeCheck',
    'tracts-leasehold': 'Lessee',
};
const CONTRACT_LAYER_ID_PREFIX = 'tracts-contract-';

// The tractLayersConfig key of a MapLegacy layer id: 'tracts-leasehold' -> 'Lessee',
// 'tracts-contract-Brine' -> 'Brine'. Undefined for a layer the settings don't cover.
export function tractLayerConfigKey(layerId: string): string | undefined {
    if (layerId.indexOf(CONTRACT_LAYER_ID_PREFIX) === 0) {
        return layerId.slice(CONTRACT_LAYER_ID_PREFIX.length) || undefined;
    }
    return MAP_LAYER_ID_TO_KEY[layerId];
}

// The contract type a MapLegacy layer draws, by its prefix: 'tracts-contract-Brine' -> 'Brine'.
// Undefined for every other layer, the oil and gas Leasehold layer included.
export function contractLayerPrefixOf(layerId: string | undefined): string | undefined {
    return layerId && layerId.indexOf(CONTRACT_LAYER_ID_PREFIX) === 0 ? layerId.slice(CONTRACT_LAYER_ID_PREFIX.length) || undefined : undefined;
}

// The layers a project shows until the GIS portal says otherwise: Leasehold, Contact Category and
// Title Status. Contract layers start off.
const DEFAULT_TRACT_LAYERS = ['Lessee', 'MinCat', 'TypeCheck'];

// Whether the project shows this tract layer. A layer's own setting wins; a layer without one shows
// only if it's a default layer, so a project whose config predates the setting (or has no config)
// opens on Leasehold, Contact Category and Title Status until its contract layers are turned on in
// the GIS portal.
export function isTractLayerEnabled(config: IMapConfig | null | undefined, key: string): boolean {
    const enabled = config?.tractLayersConfig?.[key]?.enabled;
    return typeof enabled === 'boolean' ? enabled : DEFAULT_TRACT_LAYERS.indexOf(key) >= 0;
}

// A config.json body as fetched -- already-parsed JSON or raw text -- as a config, or null when it
// isn't a JSON object.
export function parseMapConfig(body: unknown): IMapConfig | null {
    let value = body;
    if (typeof value === 'string') {
        try {
            value = JSON.parse(value);
        } catch {
            return null;
        }
    }
    return value && typeof value === 'object' && !Array.isArray(value) ? (value as IMapConfig) : null;
}

// A copy of `config` with each listed layer's `enabled` flag set, everything else in it untouched.
export function withTractLayersEnabled(config: IMapConfig | null | undefined, enabled: Record<string, boolean>): IMapConfig {
    const tractLayersConfig: NonNullable<IMapConfig['tractLayersConfig']> = { ...(config?.tractLayersConfig ?? {}) };
    Object.keys(enabled).forEach((key) => {
        tractLayersConfig[key] = { ...(tractLayersConfig[key] ?? {}), enabled: enabled[key] };
    });
    return { ...(config ?? {}), tractLayersConfig };
}

export async function loadMapConfig(configUrl?: string): Promise<IMapConfig | null> {
    if (!configUrl) {
        return null;
    }

    try {
        const response = await fetch(configUrl);
        if (!response.ok) {
            console.log('[MapConfig] failed to fetch config:', response.status, response.statusText);
            return null;
        }
        const raw = await response.json();
        return raw as IMapConfig;
    } catch (err) {
        console.log('[MapConfig] failed to parse config:', err);
        return null;
    }
}

// Returns an RGBA array with alpha as a 0–1 fraction (matches the existing
// Default*MapColors arrays and what SimpleFillSymbol expects).
// Accepts inputs with alpha in either 0–1 or 0–255 form (detected by `a > 1`).
export function normalizeRgba(rgba: number[] | undefined | null): number[] | undefined {
    if (!rgba || rgba.length < 3) {
        return undefined;
    }
    const r = rgba[0];
    const g = rgba[1];
    const b = rgba[2];
    let a = rgba.length >= 4 ? rgba[3] : 1;
    if (a > 1) {
        a = a / 255;
    }
    return [r, g, b, a];
}

// Multiplies alpha by `opacity` (0–1). If opacity is undefined, returns rgba unchanged.
export function applyOpacity(rgba: number[], opacity: number | undefined): number[] {
    if (opacity === undefined) {
        return rgba;
    }
    const a = (rgba[3] ?? 1) * opacity;
    return [rgba[0], rgba[1], rgba[2], a];
}

// Convenience: convert a fraction-alpha rgba to 0–255 alpha (CIM symbols expect this).
export function toCimAlpha(rgba: number[]): number[] {
    const a = rgba[3] ?? 1;
    return [rgba[0], rgba[1], rgba[2], a > 1 ? a : a * 255];
}



// WEBPACK FOOTER //
// ./src/components/project/sections/GISComponents/MapConfig.ts